import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const persistSession = (token, user) => {
  localStorage.setItem('token', token);
  localStorage.setItem('user', JSON.stringify(user));
  api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const applyToken = useCallback(async (token) => {
    const response = await api.get('/api/userauth/session', {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true,
    });
    const data = response.data;
    const userData = { username: data.username, email: data.email, roles: data.roles };
    persistSession(data.token || token, userData);
    setIsAuthenticated(true);
    setUser(userData);
    return data;
  }, []);

  useEffect(() => {
    const bootstrap = async () => {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      if (token && userData) {
        persistSession(token, JSON.parse(userData));
        setIsAuthenticated(true);
        setUser(JSON.parse(userData));
        setLoading(false);
        return;
      }
      try {
        const response = await api.get('/api/userauth/session', { withCredentials: true });
        const data = response.data;
        const sessionUser = { username: data.username, email: data.email, roles: data.roles };
        persistSession(data.token, sessionUser);
        setIsAuthenticated(true);
        setUser(sessionUser);
      } catch {
        // not signed in
      } finally {
        setLoading(false);
      }
    };
    bootstrap();
  }, []);

  const login = async (username, password) => {
    try {
      const response = await api.post('/api/userauth/login', { username, password }, { withCredentials: true });
      const { token, username: userUsername, email, roles } = response.data;
      const userData = { username: userUsername, email, roles };
      persistSession(token, userData);
      setIsAuthenticated(true);
      setUser(userData);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Login failed' };
    }
  };

  const register = async (userData) => {
    try {
      await api.post('/api/userauth/register', userData);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data || 'Registration failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
    setIsAuthenticated(false);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, register, logout, loading, applyToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const getToken = () => localStorage.getItem('token');
